// ============ PROJECTS ============
// Single source of truth for everything that appears in the work sections.
//
// `featured: true` puts a project on the homepage. Keep it to four — the
// homepage is a highlight reel, /work is the full list. Flip the flag to
// swap which four lead.
//
// `group` drives the headings on /work. Two groups, split by what a client
// cares about (ownership) rather than by technology, which overlaps:
//   'products' — things I own and operate
//   'systems'  — infrastructure and agents built to run inside other stacks
//   'mobile'   — iOS and Android apps, shipped or in progress
//
// A project with no `link` is not shipped yet. It renders as a plain card (no
// dead '#' anchor), the status checker skips it, and it stays out of every
// "N products shipped" count. Its `status` is the only badge it gets, so keep
// it to what is true today: 'In development' or 'Beta'.
//
// Keep `tags` to three. More reads as noise and stops being scannable.
//
// `status: 'Live'` is the authored claim and only a fallback. The real badge
// comes from frontend/public/status.json, written daily by scripts/check-status.mjs
// — see frontend/src/hooks/useLiveStatus.js for which one wins when.
//
// scripts/check-status.mjs imports this file from plain Node, which reads it as
// ESM through module detection. See the note in caseStudies.js for why these
// shared data files are .js rather than .mjs.
//
// `slug` ties a project to its screenshot, its recorded demo clip, and its
// entry in scripts/record-demos.mjs. Keep it stable.
//
// `sandbox` is the only place a visitor is invited to press something on a live
// product, so it points at somewhere that cannot lose anyone money or leave a
// trace: a read-only market view, a testnet vault, a public preview endpoint.
// If a project has no such surface, it has no sandbox, and the card just links
// out. Nothing is listed here that has not been opened and checked by hand:
//
//   sandbox: {
//     href: 'https://predectium.com/market/<some-open-market>?view=readonly',
//     label: 'Open a live market',
//     note: 'Read-only — nothing to sign',
//   },
//
// `mediaNote` is a short label shown on the card's media when the screenshot or
// clip shows sample or prototype data. Anything filmed from a build that is not
// running on real data gets one.
//
// `caseStudy` is the slug of a page in caseStudies.js. A project with one gets
// a second link on /work — the live site is still the primary link, because the
// running thing beats the write-up about it.

export const projects = [
  {
    title: 'Lobstack.ai',
    slug: 'lobstack',
    summary:
      'One endpoint for 26 models, with a receipt on every call: which model served it and what it cost.',
    tags: ['Agent infra', 'Gateway', 'Dev tool'],
    status: 'Live',
    link: 'https://lobstack.ai',
    image: '/images/showcase/lobstack.jpg',
    group: 'products',
    featured: true,
    caseStudy: 'lobstack',
  },
  {
    title: 'Arcis.money',
    slug: 'arcis',
    summary:
      'The treasury layer that lets any AI agent earn USDC through yield, credit, and revenue bonds.',
    tags: ['Agent payments', 'Fintech', 'x402'],
    status: 'Live',
    link: 'https://www.arcis.money',
    group: 'systems',
    featured: true,
    image: '/images/showcase/arcis.jpg',
  },
  {
    title: 'Sentric.sh',
    slug: 'sentric',
    summary:
      'Signals API that turns Solana KOL activity into structured, tradeable intelligence.',
    tags: ['Signals API', 'Solana', 'x402'],
    status: 'Live',
    link: 'https://www.sentric.sh',
    group: 'systems',
    featured: true,
    image: '/images/showcase/sentric.jpg',
  },
  {
    title: 'Predectium',
    slug: 'predectium',
    summary:
      'Prediction markets platform built directly on the Polymarket CLOB.',
    tags: ['Prediction markets', 'Trading', 'Web3'],
    status: 'Live',
    link: 'https://predectium.com',
    group: 'products',
    featured: true,
    image: '/images/showcase/predectium.jpg',
  },
  {
    title: 'Palmi.app',
    slug: 'palmi',
    summary:
      'Mobile app for private circles to network and share a daily ritual.',
    tags: ['Consumer', 'Mobile', 'AI'],
    status: 'Live',
    link: 'https://palmi.app',
    group: 'mobile',
    image: '/images/showcase/palmi.jpg',
  },
  {
    title: 'Tradevanish.com',
    slug: 'tradevanish',
    summary: 'Copy-trading platform with residential IP isolation per account.',
    tags: ['Fintech', 'Trading', 'SaaS'],
    status: 'Live',
    link: 'https://tradevanish.com',
    group: 'products',
    image: '/images/showcase/tradevanish.jpg',
  },
  {
    title: 'AskQuanti',
    slug: 'askquanti',
    summary:
      'Multi-agent research terminal covering stocks, ETFs, commodities, and crypto.',
    tags: ['Multi-agent', 'Research', 'Quant'],
    status: 'Live',
    link: 'https://askquanti.com',
    image: '/images/showcase/askquanti.jpg',
    group: 'products',
  },
  {
    title: 'AskOgma',
    slug: 'askogma',
    summary:
      'Conversational research agent that answers from your own documents and cites what it used.',
    tags: ['RAG', 'Research', 'Agents'],
    status: 'Live',
    link: 'https://askogma.com',
    image: '/images/showcase/askogma.jpg',
    group: 'products',
  },
  {
    title: 'ReplyLoop',
    slug: 'replyloop',
    summary:
      'Answers your Google, Yelp, Trustpilot and G2 reviews in your voice, through Claude.',
    tags: ['Agents', 'Automation', 'Claude'],
    status: 'Live',
    link: 'https://replyloop.dev',
    group: 'systems',
    image: '/images/showcase/replyloop.jpg',
  },
  {
    title: 'Binacci',
    slug: 'binacci',
    summary: 'On-chain trading agent with institutional execution and strategy skills.',
    tags: ['Trading agent', 'On-chain', 'Automation'],
    status: 'Live',
    link: 'https://binacci.ai',
    // binacci.ai opens on its PROPQUANT demo terminal; the figures are not live.
    mediaNote: 'Demo data',
    group: 'systems',
    image: '/images/showcase/binacci.jpg',
  },
  {
    title: 'TradeCafe.ai',
    slug: 'tradecafe',
    summary: 'Quantitative intelligence ecosystem for active traders.',
    tags: ['Quant', 'Analysis', 'Trading'],
    status: 'Live',
    link: 'https://tradecafe.ai/join/8J8RHBDA',
    group: 'products',
    image: '/images/showcase/tradecafe.jpg',
  },
  {
    title: 'GLOWUP',
    slug: 'glowup',
    summary:
      'AI appearance coach: one selfie becomes a 30-day hair and grooming plan.',
    tags: ['Consumer', 'Mobile', 'AI coaching'],
    status: 'In development',
    group: 'mobile',
    // Demo is filmed from the Expo web build: scripts/record-app-demos.mjs
    image: '/images/showcase/glowup.jpg',
    // The app runs on local prototype results until the analysis worker is
    // wired in; the backend is real, the numbers on screen are not yet.
    mediaNote: 'Prototype data',
    caseStudy: 'glowup',
  },
  {
    title: 'ROAM',
    slug: 'roam',
    summary:
      'Ranks where and when to travel. Engines compute every score; the model only explains them.',
    tags: ['Travel', 'Mobile', 'Scoring'],
    status: 'In development',
    group: 'mobile',
    image: '/images/showcase/roam.jpg',
    // Filmed from ROAM's deployed web build, which renders sample opportunities
    // until its ranking API has a database. The card has to say that.
    mediaNote: 'Sample data',
    caseStudy: 'roam',
  },
  {
    title: 'Otto',
    slug: 'otto',
    summary:
      'AI assistant that makes your calls, says it is an AI, and cannot spend money.',
    tags: ['Voice agent', 'Mobile', 'Consumer'],
    status: 'In development',
    group: 'mobile',
    image: '/images/showcase/otto.jpg',
    // The app runs on its local demo store: the requests in the clip are
    // seeded, and Otto has not placed a real call yet.
    mediaNote: 'Prototype data',
    caseStudy: 'otto',
  },
];

export const visibleProjects = projects.filter((p) => !p.hidden);
const visible = visibleProjects;

export const featuredProjects = visible.filter((p) => p.featured);

export const productProjects = visible.filter((p) => p.group === 'products');
export const systemProjects = visible.filter((p) => p.group === 'systems');
// Live apps first, then the ones still being built.
export const mobileProjects = visible
  .filter((p) => p.group === 'mobile')
  .sort((a, b) => Number(Boolean(b.link)) - Number(Boolean(a.link)));

// Shipped means there is something to open. Counts that say "shipped" use this.
export const shippedProjects = visible.filter((p) => p.link);
export const inProgressProjects = visible.filter((p) => !p.link);

export default projects;
